import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';

type SermonHeroForm = {
  label: string;
  title: string;
  meta: string;
};

type SermonVideoRow = {
  id: number;
  badge: string;
  title: string;
  speaker: string;
  duration: string;
  summary: string;
  series: string;
  thumbnailUrl: string;
  videoUrl: string;
};

type SermonVideoForm = {
  badge: string;
  title: string;
  speaker: string;
  duration: string;
  summary: string;
  series: string;
  thumbnailUrl: string;
  videoUrl: string;
};

const EMPTY_HERO: SermonHeroForm = { label: '', title: '', meta: '' };
const EMPTY_VIDEO_FORM: SermonVideoForm = {
  badge: '',
  title: '',
  speaker: '',
  duration: '',
  summary: '',
  series: '',
  thumbnailUrl: '',
  videoUrl: '',
};

export default function ManageSermon({ authChurch }: { authChurch: string }) {
  const [heroForm, setHeroForm] = useState<SermonHeroForm>(EMPTY_HERO);
  const [heroLoading, setHeroLoading] = useState(true);
  const [heroSaving, setHeroSaving] = useState(false);
  const [heroMessage, setHeroMessage] = useState<string | null>(null);
  const [heroDraft, setHeroDraft] = useState<SermonHeroForm | null>(null);

  const [videos, setVideos] = useState<SermonVideoRow[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<SermonVideoForm>(EMPTY_VIDEO_FORM);
  const [saving, setSaving] = useState(false);

  const [activeBadge, setActiveBadge] = useState('전체');
  const [search, setSearch] = useState('');

  const fetchHero = () => {
    setHeroLoading(true);
    axios
      .get(`${MainURL}/sermon/hero`, { params: { authChurch } })
      .then((res) => {
        const data = res.data?.data;
        setHeroForm(data ? { label: data.label ?? '', title: data.title ?? '', meta: data.meta ?? '' } : EMPTY_HERO);
      })
      .catch((err) => console.error('GET /sermon/hero', err))
      .finally(() => setHeroLoading(false));
  };

  const fetchVideos = () => {
    setListLoading(true);
    setListError(null);
    axios
      .get(`${MainURL}/sermon/video`, { params: { authChurch } })
      .then((res) => setVideos(res.data?.data ?? []))
      .catch((err) => {
        console.error('GET /sermon/video', err);
        setListError('설교 영상을 불러오지 못했습니다.');
      })
      .finally(() => setListLoading(false));
  };

  useEffect(() => {
    fetchHero();
    fetchVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChurch]);

  const openHeroForm = () => {
    setHeroMessage(null);
    setHeroDraft(heroForm);
  };

  const saveHero = () => {
    if (!heroDraft) return;
    setHeroSaving(true);
    setHeroMessage(null);
    axios
      .put(`${MainURL}/sermon/hero`, { authChurch, ...heroDraft })
      .then(() => {
        setHeroForm(heroDraft);
        setHeroDraft(null);
      })
      .catch((err) => {
        console.error('PUT /sermon/hero', err);
        setHeroMessage('저장에 실패했습니다.');
      })
      .finally(() => setHeroSaving(false));
  };

  const openCreateForm = () => {
    setEditingId(null);
    setForm(EMPTY_VIDEO_FORM);
    setFormOpen(true);
  };

  const openEditForm = (video: SermonVideoRow) => {
    setEditingId(video.id);
    setForm({
      badge: video.badge,
      title: video.title,
      speaker: video.speaker,
      duration: video.duration,
      summary: video.summary,
      series: video.series,
      thumbnailUrl: video.thumbnailUrl,
      videoUrl: video.videoUrl ?? '',
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setForm(EMPTY_VIDEO_FORM);
  };

  const submitForm = () => {
    if (!form.title.trim()) return;
    setSaving(true);
    const request = editingId
      ? axios.put(`${MainURL}/sermon/video/${editingId}`, { authChurch, ...form })
      : axios.post(`${MainURL}/sermon/video`, { authChurch, ...form });
    request
      .then(() => {
        closeForm();
        fetchVideos();
      })
      .catch((err) => {
        console.error(editingId ? 'PUT /sermon/video/:id' : 'POST /sermon/video', err);
        window.alert(err.response?.data?.message || '저장에 실패했습니다.');
      })
      .finally(() => setSaving(false));
  };

  const removeVideo = (id: number) => {
    if (!window.confirm('이 설교 영상을 삭제할까요?')) return;
    axios
      .delete(`${MainURL}/sermon/video/${id}`, { params: { authChurch } })
      .then(() => fetchVideos())
      .catch((err) => console.error('DELETE /sermon/video/:id', err));
  };

  const moveVideo = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= videos.length) return;
    const next = [...videos];
    [next[index], next[target]] = [next[target], next[index]];
    setVideos(next);
    axios
      .put(`${MainURL}/sermon/video/reorder`, { authChurch, orderedIds: next.map((row) => row.id) })
      .catch((err) => console.error('PUT /sermon/video/reorder', err));
  };

  const badgeOptions = useMemo(
    () => Array.from(new Set(videos.map((v) => v.badge).filter(Boolean))),
    [videos],
  );

  const filteredVideos = useMemo(() => {
    const keyword = search.trim();
    return videos.filter((video) => {
      if (activeBadge !== '전체' && video.badge !== activeBadge) return false;
      if (keyword && !video.title.includes(keyword) && !video.speaker.includes(keyword)) return false;
      return true;
    });
  }, [videos, activeBadge, search]);

  const seriesBreakdown = useMemo(() => {
    const counts = new Map<string, number>();
    videos.forEach((v) => {
      const key = v.series.trim();
      if (!key) return;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [videos]);

  const SERIES_DOT_COLORS = ['#1d4ed8', '#6d28d9', '#047857', '#b45309', '#be185d', '#0f766e'];

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">설교영상</h2>
          <p className="manage-panel-desc">앱에 노출되는 설교 영상을 관리합니다.</p>
        </div>
        <button type="button" className="manage-btn manage-btn--primary" onClick={openHeroForm} disabled={heroLoading}>
          상단 히어로 수정하기
        </button>
      </div>

      {!listLoading && !listError && videos.length > 0 && (
        <div className="manage-filterbar">
          <div className="manage-seg">
            {['전체', ...badgeOptions].map((badge) => (
              <button
                key={badge}
                type="button"
                className={`manage-seg-btn${activeBadge === badge ? ' is-active' : ''}`}
                onClick={() => setActiveBadge(badge)}
              >
                {badge}
              </button>
            ))}
          </div>
          <div className="manage-filterbar-search">
            <input
              placeholder="제목, 설교자로 검색"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      )}

      <div className="manage-grid-2col">
      <section className="manage-card-block">
        <div className="manage-block-head">
          <h3 className="manage-block-title">설교 영상 목록</h3>
          <span className="manage-block-count">{filteredVideos.length}</span>
          <div className="manage-block-actions">
            <button type="button" className="manage-btn manage-btn--sm manage-btn--primary" onClick={openCreateForm}>
              + 새 설교영상 등록
            </button>
          </div>
        </div>

        {listLoading && <div className="manage-state">불러오는 중입니다...</div>}
        {!listLoading && listError && <div className="manage-state manage-state--error">{listError}</div>}

        {!listLoading && !listError && (
          <>
            <div className="manage-news-list">
              {videos.length === 0 && (
                <div className="manage-table-empty">등록된 설교 영상이 없습니다.</div>
              )}
              {videos.length > 0 && filteredVideos.length === 0 && (
                <div className="manage-table-empty">검색 결과가 없습니다.</div>
              )}
              {filteredVideos.map((video) => {
                const index = videos.findIndex((v) => v.id === video.id);
                const thumbVariant = (index % 4) + 1;
                return (
                  <div className="manage-worship-row" key={video.id}>
                    <div className="manage-move-btns manage-move-btns--row manage-worship-row-move">
                      <button type="button" onClick={() => moveVideo(index, -1)} disabled={index === 0}>▲</button>
                      <button type="button" onClick={() => moveVideo(index, 1)} disabled={index === videos.length - 1}>▼</button>
                    </div>
                    <div className={`manage-sermon-thumb manage-sermon-thumb--${thumbVariant}`}>
                      {video.thumbnailUrl ? (
                        <img className="manage-sermon-thumb-img" src={video.thumbnailUrl} alt="" />
                      ) : (
                        '▶'
                      )}
                      {video.duration && <span className="manage-sermon-thumb-dur">{video.duration}</span>}
                    </div>
                    <div className="manage-sermon-row-info">
                      <div className="manage-worship-row-title">{video.title}</div>
                      <div className="manage-sermon-row-meta">
                        {video.speaker && <span>🎤 {video.speaker}</span>}
                        {video.badge && <span>⛪ {video.badge}</span>}
                      </div>
                      {video.series && (
                        <div className="manage-sermon-row-tags">
                          <span className="manage-badge manage-badge--blue">시리즈: {video.series}</span>
                        </div>
                      )}
                      {video.summary && <p className="manage-news-summary">{video.summary}</p>}
                      {video.videoUrl ? (
                        <a className="manage-sermon-row-link" href={video.videoUrl} target="_blank" rel="noopener noreferrer">
                          🔗 {video.videoUrl}
                        </a>
                      ) : (
                        <span className="manage-sermon-row-link manage-sermon-row-link--empty">영상 URL 미입력 — 앱에서 눌러도 열리지 않습니다</span>
                      )}
                    </div>
                    <div className="manage-worship-row-actions">
                      <button type="button" className="manage-worship-icon-btn" onClick={() => openEditForm(video)} title="수정">✏️</button>
                      <button type="button" className="manage-worship-icon-btn" onClick={() => removeVideo(video.id)} title="삭제">🗑</button>
                    </div>
                  </div>
                );
              })}
            </div>

          </>
        )}
      </section>

      {!listLoading && !listError && (
        <div className="manage-side-stack">
          <div className="manage-side-card manage-worship-phone">
            <div className="manage-worship-phone-notch">
              {heroForm.label || '설교영상'}
              <small>앱 설교영상 탭 미리보기</small>
            </div>
            <div className="manage-worship-phone-screen">
              {videos.length === 0 && <div className="manage-side-empty">등록된 설교 영상이 없습니다.</div>}
              {videos.slice(0, 4).map((video, index) => (
                <div className="manage-sermon-phone-row" key={video.id}>
                  <div className={`manage-sermon-thumb manage-sermon-thumb--${(index % 4) + 1} manage-sermon-thumb--sm`}>
                    {video.thumbnailUrl ? (
                      <img className="manage-sermon-thumb-img" src={video.thumbnailUrl} alt="" />
                    ) : (
                      '▶'
                    )}
                    {video.duration && <span className="manage-sermon-thumb-dur">{video.duration}</span>}
                  </div>
                  <div className="manage-sermon-phone-info">
                    <b>{video.title}</b>
                    <span>{[video.speaker, video.badge].filter(Boolean).join(' · ')}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="manage-side-card">
            <div className="manage-side-card-head">📚 시리즈별 현황</div>
            <div className="manage-side-card-body">
              {seriesBreakdown.length === 0 && <p className="manage-side-empty">등록된 시리즈가 없습니다.</p>}
              {seriesBreakdown.map((series, index) => (
                <div className="manage-stat-row" key={series.name}>
                  <span
                    className="manage-stat-row-dot"
                    style={{ background: SERIES_DOT_COLORS[index % SERIES_DOT_COLORS.length] }}
                  />
                  <span className="manage-stat-row-label">{series.name}</span>
                  <span className="manage-stat-row-count">{series.count}편</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      </div>

      {heroDraft && (
        <div className="manage-modal-overlay" onClick={() => setHeroDraft(null)}>
          <div className="manage-modal" onClick={(e) => e.stopPropagation()}>
            <div className="manage-modal-header">
              <h3>상단 히어로 수정</h3>
              <button type="button" className="manage-modal-close" onClick={() => setHeroDraft(null)}>×</button>
            </div>
            <div className="manage-modal-body">
              <p className="manage-modal-desc">
                상단 히어로는 성도 앱 설교영상 화면 맨 위에 크게 보이는 대표 설교 카드입니다. 라벨·제목·부가정보 순서로
                표시되며, 세 칸이 모두 비어 있으면 카드가 나타나지 않습니다.
                <br />
                교회 전체에 하나만 있고, 저장하면 성도 앱에 바로 반영됩니다.
              </p>
              <div className="manage-form">
                <label className="manage-field">
                  <span>라벨</span>
                  <input
                    value={heroDraft.label}
                    onChange={(e) => setHeroDraft({ ...heroDraft, label: e.target.value })}
                    placeholder="예: 주일설교"
                  />
                </label>
                <label className="manage-field">
                  <span>제목</span>
                  <input
                    value={heroDraft.title}
                    onChange={(e) => setHeroDraft({ ...heroDraft, title: e.target.value })}
                    placeholder="예: 세상의 빛으로 살아가기"
                  />
                </label>
                <label className="manage-field">
                  <span>부가정보</span>
                  <input
                    value={heroDraft.meta}
                    onChange={(e) => setHeroDraft({ ...heroDraft, meta: e.target.value })}
                    placeholder="예: 김온누리 목사 · 2026.06.07 · 38분 · 마태복음 5:13-16"
                  />
                </label>
                {heroMessage && <span className="manage-inline-message">{heroMessage}</span>}
              </div>
            </div>
            <div className="manage-modal-footer">
              <button type="button" className="manage-btn" onClick={() => setHeroDraft(null)}>취소</button>
              <button type="button" className="manage-btn manage-btn--primary" onClick={saveHero} disabled={heroSaving}>
                {heroSaving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {formOpen && (
        <div className="manage-modal-overlay" onClick={closeForm}>
          <div className="manage-modal" onClick={(e) => e.stopPropagation()}>
            <div className="manage-modal-header">
              <h3>{editingId ? '설교영상 수정' : '새 설교영상 등록'}</h3>
              <button type="button" className="manage-modal-close" onClick={closeForm}>×</button>
            </div>
            <div className="manage-modal-body">
              <div className="manage-form manage-form--grid">
                <label className="manage-field">
                  <span>배지</span>
                  <input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} placeholder="예: 주일설교" />
                </label>
                <label className="manage-field">
                  <span>설교자</span>
                  <input value={form.speaker} onChange={(e) => setForm({ ...form, speaker: e.target.value })} placeholder="예: 김온누리 목사" />
                </label>
                <label className="manage-field manage-field--full">
                  <span>제목</span>
                  <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="예: 세상의 빛으로 살아가기" />
                </label>
                <label className="manage-field">
                  <span>길이</span>
                  <input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="예: 38분" />
                </label>
                <label className="manage-field">
                  <span>시리즈</span>
                  <input value={form.series} onChange={(e) => setForm({ ...form, series: e.target.value })} placeholder="예: 염려에서 평안으로" />
                </label>
                <label className="manage-field manage-field--full">
                  <span>요약</span>
                  <textarea rows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
                </label>
                <label className="manage-field manage-field--full">
                  <span>설교 영상 URL</span>
                  <input value={form.videoUrl} onChange={(e) => setForm({ ...form, videoUrl: e.target.value })} placeholder="https://youtu.be/..." />
                </label>
                <label className="manage-field manage-field--full">
                  <span>썸네일 이미지 URL</span>
                  <input value={form.thumbnailUrl} onChange={(e) => setForm({ ...form, thumbnailUrl: e.target.value })} placeholder="https://..." />
                </label>
              </div>
            </div>
            <div className="manage-modal-footer">
              <button type="button" className="manage-btn" onClick={closeForm}>취소</button>
              <button
                type="button"
                className="manage-btn manage-btn--primary"
                onClick={submitForm}
                disabled={saving || !form.title.trim()}
              >
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
